/**
 * Platform admin card: purchasable credit packs. Seed/sync from environment,
 * toggle availability, delete (/api/credits/admin/packs).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { ADMIN_COLORS } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useToast } from '../../context/ToastContext';
import { confirmDialog, showDialog } from '../../utils/dialogs';

export interface CreditPack {
  id: number;
  name: string;
  credits: number;
  display_order: number;
  stripe_product_id: string | null;
  stripe_price_id_usd: string | null;
  stripe_price_id_eur: string | null;
  stripe_price_id_try: string | null;
  price_cents_usd: number | null;
  price_cents_eur: number | null;
  price_cents_try: number | null;
  is_active: boolean;
  available_for_b2c: boolean;
  available_for_b2b2c: boolean;
}

export default function CreditPacksCard() {
  const { showToast } = useToast();
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [creditPacksLoading, setCreditPacksLoading] = useState(false);
  const [seedingPacks, setSeedingPacks] = useState(false);
  const [togglingPackId, setTogglingPackId] = useState<number | null>(null);
  const [deletingPackId, setDeletingPackId] = useState<number | null>(null);

  const loadCreditPacks = useCallback(async () => {
    try {
      setCreditPacksLoading(true);
      const result = await apiRequest<{ packs: CreditPack[] }>({
        endpoint: '/credits/admin/packs',
      });
      setCreditPacks(result.packs);
    } catch (error: any) {
      console.error('Failed to load credit packs:', error);
    } finally {
      setCreditPacksLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCreditPacks();
  }, [loadCreditPacks]);

  const handleSeedCreditPacks = async () => {
    try {
      setSeedingPacks(true);
      const result = await apiRequest<{ seeded: number; packs: { id: number; name: string; credits: number; action: string; has_usd_price: boolean; has_eur_price: boolean; has_try_price: boolean }[] }>({
        endpoint: '/credits/admin/packs/seed',
        method: 'POST',
      });
      const created = result.packs.filter(p => p.action === 'created').length;
      const updated = result.packs.filter(p => p.action === 'updated').length;
      showToast(`Seeded ${result.seeded} packs (${created} created, ${updated} updated)`, 'success');
      await loadCreditPacks();
    } catch (error: any) {
      const message = error?.message || 'Failed to seed credit packs';
      showDialog(message);
    } finally {
      setSeedingPacks(false);
    }
  };

  const handleTogglePack = async (pack: CreditPack) => {
    try {
      setTogglingPackId(pack.id);
      await apiRequest({
        endpoint: `/credits/admin/packs/${pack.id}`,
        method: 'PUT',
        data: { is_active: !pack.is_active },
      });
      await loadCreditPacks();
    } catch (error: any) {
      const message = error?.message || 'Failed to update pack';
      showDialog(message);
    } finally {
      setTogglingPackId(null);
    }
  };

  const handleDeletePack = async (pack: CreditPack) => {
    const doDelete = async () => {
      try {
        setDeletingPackId(pack.id);
        await apiRequest({
          endpoint: `/credits/admin/packs/${pack.id}`,
          method: 'DELETE',
        });
        await loadCreditPacks();
      } catch (error: any) {
        const message = error?.message || 'Failed to delete pack';
        showDialog(message);
      } finally {
        setDeletingPackId(null);
      }
    };

    const confirmed = await confirmDialog(
      'Delete Pack',
      `Delete "${pack.name}"? This cannot be undone.`,
      'Delete',
      'Cancel',
    );
    if (confirmed) await doDelete();
  };

  return (
    <Card>
      <CardHeader className="pb-0">
        <Text className="text-lg font-semibold text-foreground">
          Credit Packs
        </Text>
        <Text className="text-sm text-muted-foreground mt-1">
          Manage purchasable credit packs. Seed from environment variables or toggle availability.
        </Text>
      </CardHeader>
      <CardContent className="gap-4">
        <Button
          variant="outline"
          disabled={seedingPacks}
          onPress={handleSeedCreditPacks}
          className="flex-row items-center justify-center gap-2"
        >
          {seedingPacks ? (
            <ActivityIndicator size="small" color="#1F2937" />
          ) : (
            <MaterialIcons name="sync" size={18} color="#1F2937" />
          )}
          <Text className="text-sm font-semibold text-foreground">
            Seed / Sync from Environment
          </Text>
        </Button>

        {creditPacksLoading ? (
          <ActivityIndicator size="small" color={ADMIN_COLORS.primary} />
        ) : creditPacks.length === 0 ? (
          <Text className="text-sm text-muted-foreground text-center py-4">
            No credit packs configured. Press {'"'}Seed / Sync{'"'} to create packs from environment variables.
          </Text>
        ) : (
          <View className="gap-3">
            {creditPacks.map((pack) => (
              <View
                key={pack.id}
                className={`border rounded-lg p-3 gap-2 ${pack.is_active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
              >
                <View className="flex-row items-center justify-between">
                  <View className="flex-1">
                    <View className="flex-row items-center gap-2">
                      <Text className="text-sm font-semibold text-foreground">
                        {pack.name}
                      </Text>
                      <View className={`px-2 py-0.5 rounded-full ${pack.is_active ? 'bg-green-100' : 'bg-gray-200'}`}>
                        <Text className={`text-xs font-medium ${pack.is_active ? 'text-green-700' : 'text-gray-500'}`}>
                          {pack.is_active ? 'Active' : 'Inactive'}
                        </Text>
                      </View>
                    </View>
                    <Text className="text-xs text-muted-foreground mt-1">
                      {pack.credits} credit{pack.credits !== 1 ? 's' : ''}
                      {pack.price_cents_try ? ` · ₺${(pack.price_cents_try / 100).toFixed(0)}` : ''}
                      {pack.price_cents_usd ? ` · $${(pack.price_cents_usd / 100).toFixed(0)}` : ''}
                      {pack.price_cents_eur ? ` · €${(pack.price_cents_eur / 100).toFixed(0)}` : ''}
                    </Text>
                    <Text className="text-xs text-muted-foreground">
                      Stripe: {pack.stripe_product_id ? '✓ Product' : '✗ No product'}
                      {pack.stripe_price_id_try ? ' · ✓ TRY' : ''}
                      {pack.stripe_price_id_usd ? ' · ✓ USD' : ''}
                      {pack.stripe_price_id_eur ? ' · ✓ EUR' : ''}
                    </Text>
                  </View>
                  <View className="flex-row gap-2">
                    <TouchableOpacity
                      disabled={togglingPackId === pack.id}
                      onPress={() => handleTogglePack(pack)}
                      className="p-2"
                    >
                      {togglingPackId === pack.id ? (
                        <ActivityIndicator size="small" color="#6b7280" />
                      ) : (
                        <MaterialIcons
                          name={pack.is_active ? 'toggle-on' : 'toggle-off'}
                          size={28}
                          color={pack.is_active ? '#10B981' : '#9CA3AF'}
                        />
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      disabled={deletingPackId === pack.id}
                      onPress={() => handleDeletePack(pack)}
                      className="p-2"
                    >
                      {deletingPackId === pack.id ? (
                        <ActivityIndicator size="small" color="#DC2626" />
                      ) : (
                        <MaterialIcons name="delete-outline" size={22} color="#DC2626" />
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </CardContent>
    </Card>
  );
}
